import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App, type BackButtonListenerEvent } from "@capacitor/app";

/**
 * Phase 2 - Android hardware/gesture back-button bridge.
 *
 * Without this, Android's predictive-back gesture and hardware back
 * button trigger Capacitor's default behaviour (exit the app), which
 * makes the app feel like a web wrapper rather than native.
 *
 * Behaviour:
 *  - Anywhere except the root route → `navigate(-1)` (history pop).
 *    React Router's navigation direction hook detects this as POP and
 *    NativePageTransition plays the slide-back animation.
 *  - On the root route (`/` or `/connect-portal/dashboard`) → fall back
 *    to `App.minimizeApp()` (sends to recents like every native Android
 *    app does), instead of killing the process.
 *
 * iOS is unaffected - there is no hardware back button there; iOS users
 * get the edge-swipe via `useEdgeSwipeBack`.
 */
const ROOT_ROUTES = new Set<string>([
  "/",
  "/connect-portal/dashboard",
  "/connect-portal/dial",
  "/connect-portal/messages",
  "/connect-portal/me",
  "/connect-portal/history",
]);

export function useAndroidBackNavigation() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let isAndroid = false;
    try {
      isAndroid =
        Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
    } catch {
      return;
    }
    if (!isAndroid) return;

    let handle: { remove: () => void } | null = null;
    let removed = false;

    const handler = (event: BackButtonListenerEvent) => {
      const path = location.pathname;
      // At a tab root → minimize, don't exit
      if (ROOT_ROUTES.has(path) || !event.canGoBack) {
        void App.minimizeApp().catch(() => {
          /* older Capacitor builds without minimizeApp - no-op */
        });
        return;
      }
      navigate(-1);
    };

    void App.addListener("backButton", handler).then((h) => {
      if (removed) {
        void h.remove();
        return;
      }
      handle = h;
    });

    return () => {
      removed = true;
      if (handle) void handle.remove();
    };
  }, [navigate, location.pathname]);
}

export default useAndroidBackNavigation;
